/**
 * Organization-scoped NATS Request Service
 * 
 * Handles NATS requests using the organization-scoped subject pattern:
 * {org-alias}.data.request
 * 
 * Request format:
 * {
 *   "request_id": "req-001",
 *   "statement_id": "PC.available_machines",
 *   "parameters": { ... },
 *   "token": "eyJhbGciOiJSUzI1NiIs..."
 * }
 */

import { natsClient } from './NATSClient';
import { getOrgAlias, getOrganizationFromToken } from '../utils/tokenUtils';
import type { OrganizationInfo } from '../utils/tokenUtils';

// Statement IDs for different queries
export const StatementIds = {
  // Machine and line data
  AVAILABLE_MACHINES: 'PC.available_machines',       // All machines (for tree view)
  MACHINES_WITH_DATA: 'PC.machines_with_data',       // Only machines with datenVerfuegbar=1
  MACHINE_STATUS: 'PC.machine_status',
  MACHINE_STATUS_PER_SHIFT: 'PC.machine_status_per_shift',
  
  // Efficiency and performance (machine-level)
  EFFICIENCY: 'PC.efficiency',
  UTILIZATION: 'PC.utilization',
  OUTPUT: 'PC.output',
  DOWNTIME_ACCUMULATED: 'PC.downtime_accumulated',
  MTBF: 'PC.mtbf',
  AVERAGE_PRODUCTION_SPEED: 'PC.average_production_speed',
  
  // Line-level aggregated KPI (single request for all KPIs)
  LINE_KPI_DATA: 'PC.linekpi_data',

  // Line-level per-KPI requests (use line_bk param; separate statement per
  // aggregation level for output)
  LINE_EFFICIENCY: 'PC.line_efficiency',
  LINE_OUTPUT: 'PC.line_output',
  LINE_OUTPUT_DAILY: 'PC.line_output_daily',
  LINE_OUTPUT_WEEKLY: 'PC.line_output_weekly',
  
  // Quality and rejects
  REJECT_RATIO: 'PC.reject_ratio',
  NUMBER_OF_REJECTS: 'PC.number_of_rejects',
  NUMBER_OF_REJECT_EVENTS: 'PC.number_of_reject_events',
  
  // Faults and downtime
  NUMBER_OF_FAULTS: 'PC.number_of_faults',
  DOWNTIME_DURATION_OF_FAULTS: 'PC.downtime_duration_of_faults',
  
  // Targets and presets
  TARGET_DATA: 'PC.target_data',
  PRESETS: 'PC.presets',
  PRESETS_DETAIL: 'PC.presets_detail',
  AVAILABLE_DATES: 'PC.available_dates',
  AVAILABLE_SHIFTS: 'PC.available_shifts',
  LINE_AVAILABLE_SHIFTS: 'PC.line_available_shifts',
  
  // Energy and condition monitoring
  ENERGY_DATA: 'PC.energy_data',
  CONDITION_MONITORING: 'PC.condition_monitoring',

  // Sensor history (Doris OLAP)
  SENSOR_HISTORY: 'PC.sensor_history',
  SENSOR_HISTORY_DETAIL: 'PC.sensor_history_detail',
  // Axis-aware variants: sensor_name is the full OPC path so multi-axis
  // (edge-16 / schema B) machines stay distinct. Used by motor/servo.
  SENSOR_HISTORY_AXIS: 'PC.sensor_history_axis',
  SENSOR_HISTORY_AXIS_DETAIL: 'PC.sensor_history_axis_detail',

  // Flow layout (machine ordering in line)
  SAVE_FLOW_LAYOUT: 'PC.save_flow_layout',

  // Chart comments
  GET_CHART_COMMENTS: 'PC.get_chart_comments',
  SAVE_CHART_COMMENT: 'PC.save_chart_comment',
  DELETE_CHART_COMMENT: 'PC.delete_chart_comment',

  // Line KPI configuration
  GET_LINE_KPI_CONFIG: 'PC.get_line_kpi_config',
  SAVE_LINE_KPI_CONFIG: 'PC.save_line_kpi_config',

  // Shift analysis (per line + single shift + single date)
  SHIFT_STOP_CAUSES: 'PC.shift_stop_causes',
  SHIFT_STOP_CAUSES_INSIGHT: 'PC.shift_stop_causes_insight',
  SHIFT_STATUS_TIMELINE: 'PC.shift_status_timeline',
  SHIFT_STATUS_TIMELINE_INSIGHT: 'PC.shift_status_timeline_insight',
  SHIFT_STOP_ATTRIBUTION: 'PC.shift_stop_attribution',
  SHIFT_TIMELINE: 'PC.shift_timeline',
  SHIFT_TIMELINE_INSIGHT: 'PC.shift_timeline_insight',
  SHIFT_MACHINE_SUMMARY: 'PC.shift_machine_summary',
  SHIFT_MACHINE_SUMMARY_INSIGHT: 'PC.shift_machine_summary_insight',
  SHIFT_LOST_PRODUCTION: 'PC.shift_lost_production',
  SHIFT_SPEED_LOSSES: 'PC.shift_speed_losses',
  SHIFT_KPI_TIMELINE: 'PC.shift_kpi_timeline',
} as const;

export type StatementId = typeof StatementIds[keyof typeof StatementIds];

export interface NATSRequest {
  request_id: string;
  statement_id: StatementId | string;
  parameters: Record<string, any>;
  user_token: string;
}

export interface NATSResponse<T = any> {
  request_id: string;
  success: boolean;
  data?: T;
  error?: string;
  error_code?: string;
  timestamp?: string;
  /** When true, the response payload is split across multiple NATS messages */
  chunked?: boolean;
  chunk_total?: number;
  total_rows?: number;
  /** Subject to subscribe to for receiving chunks */
  chunk_subject?: string;
  duration?: string;
}

/**
 * Generate a unique request ID
 */
function generateRequestId(): string {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 11);
  return `req-${timestamp}-${random}`;
}

/**
 * Organization-scoped NATS request client
 */
class OrgNATSClient {
  private static readonly REQUEST_TIMEOUT = 15000; // 15 seconds
  private static readonly MAX_RETRIES = 2;

  /**
   * Get cached organization info from token
   */
  private cachedOrgInfo: OrganizationInfo | null = null;
  private cachedToken: string | null = null;

  getOrgInfo(token: string): OrganizationInfo | null {
    if (this.cachedToken === token && this.cachedOrgInfo) {
      return this.cachedOrgInfo;
    }
    
    this.cachedOrgInfo = getOrganizationFromToken(token);
    this.cachedToken = token;
    return this.cachedOrgInfo;
  }

  /**
   * Make an organization-scoped NATS request
   */
  async request<T = any>(
    statementId: StatementId | string,
    parameters: Record<string, any>,
    token: string,
    options?: {
      timeout?: number;
      retries?: number;
      service?: 'data' | 'cockpitservice';
    }
  ): Promise<NATSResponse<T>> {
    const timeout = options?.timeout ?? OrgNATSClient.REQUEST_TIMEOUT;
    const maxRetries = options?.retries ?? OrgNATSClient.MAX_RETRIES;
    const service = options?.service ?? 'data';
    
    console.log(`📡 OrgNATSClient: Request for ${statementId}`, { parameters });
    
    // Get organization alias from token
    const orgAlias = getOrgAlias(token);
    if (!orgAlias) {
      console.error('❌ OrgNATSClient: Could not extract organization alias from token');
      return {
        request_id: generateRequestId(),
        success: false,
        error: 'Could not determine organization from token',
        error_code: 'INVALID_TOKEN',
      };
    }

    // Build the subject: {org-alias}.{service}.request
    const subject = `${orgAlias}.${service}.request`;
    const requestId = generateRequestId();

    // Build the request payload
    const request: NATSRequest = {
      request_id: requestId,
      statement_id: statementId,
      parameters,
      user_token: token,
    };

    // Retry logic
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        if (!natsClient.isConnected()) {
          console.warn(`⚠️ OrgNATSClient: NATS not connected (attempt ${attempt + 1})`);
          throw new Error('NATS not connected');
        }

        console.log(`📡 OrgNATSClient: Sending to ${subject}`, { requestId, statementId });
        const response = await natsClient.request(subject, request, timeout) as NATSResponse<T>;
        console.log(`📡 OrgNATSClient: Response for ${statementId}:`, response?.success ? 'SUCCESS' : 'FAILED', response?.data);

        return response;

      } catch (error) {
        console.error(`❌ OrgNATSClient: Attempt ${attempt + 1}/${maxRetries + 1} failed:`, error);
        
        if (attempt === maxRetries) {
          // Final attempt failed
          return {
            request_id: requestId,
            success: false,
            error: error instanceof Error ? error.message : String(error),
            error_code: 'REQUEST_FAILED',
          };
        }

        // Wait before retry
        const waitTime = (attempt + 1) * 2000;
        await new Promise(resolve => setTimeout(resolve, waitTime));
      }
    }

    // Should not reach here
    return {
      request_id: requestId,
      success: false,
      error: 'Unexpected error',
      error_code: 'UNKNOWN_ERROR',
    };
  }

  /**
   * Make a request that may return a chunked response.
   * If the response has `chunked: true`, subscribes to the chunk subject,
   * collects all chunks, and returns the concatenated data array.
   */
  async requestChunked<T = any>(
    statementId: StatementId | string,
    parameters: Record<string, any>,
    token: string,
    options?: { timeout?: number },
  ): Promise<NATSResponse<T[]>> {
    const timeout = options?.timeout ?? 30000;
    const envelope = await this.request<T[]>(statementId, parameters, token, { timeout });

    if (!envelope.success || !envelope.chunked || !envelope.chunk_subject || !envelope.chunk_total) {
      return envelope;
    }

    // Chunked response — collect all chunks
    const chunkTotal = envelope.chunk_total;
    const chunks: T[][] = new Array(chunkTotal);
    let received = 0;

    return new Promise<NATSResponse<T[]>>((resolve) => {
      const chunkTimeout = setTimeout(() => {
        sub?.unsubscribe();
        resolve({
          request_id: envelope.request_id,
          success: false,
          error: `Chunk timeout: received ${received}/${chunkTotal} chunks`,
          error_code: 'CHUNK_TIMEOUT',
        });
      }, timeout);

      let sub: { unsubscribe: () => void } | null = null;

      natsClient.subscribe(envelope.chunk_subject!, (data: Uint8Array) => {
        try {
          const chunk = JSON.parse(new TextDecoder().decode(data));
          if (chunk.chunk_index !== undefined && chunk.data) {
            chunks[chunk.chunk_index] = chunk.data;
            received++;
            if (received === chunkTotal) {
              clearTimeout(chunkTimeout);
              sub?.unsubscribe();
              const allData = chunks.flat() as T[];
              resolve({
                request_id: envelope.request_id,
                success: true,
                data: allData,
                total_rows: envelope.total_rows,
                duration: envelope.duration,
              });
            }
          }
        } catch (e) {
          console.warn('OrgNATSClient: chunk parse error', e);
        }
      }).then(s => { sub = s; });
    });
  }

  /**
   * Convenience method: Fetch available machines (organization tree)
   * @deprecated Use fetchAvailableMachines instead
   */
  async fetchSAPData(token: string, filter?: { kundengruppeGeschaeftsfeld?: string }): Promise<NATSResponse> {
    return this.request(
      StatementIds.AVAILABLE_MACHINES,
      {
        kundengruppeGeschaeftsfeld: filter?.kundengruppeGeschaeftsfeld || 'Tabak',
        ...filter,
      },
      token
    );
  }

  /**
   * Convenience method: Fetch available machines
   */
  async fetchAvailableMachines(token: string, parameters?: Record<string, any>): Promise<NATSResponse> {
    return this.request(
      StatementIds.AVAILABLE_MACHINES,
      parameters || {},
      token
    );
  }

  /**
   * Convenience method: Fetch efficiency data
   */
  async fetchEfficiencyData(
    token: string,
    parameters: Record<string, any>
  ): Promise<NATSResponse> {
    return this.request(
      StatementIds.EFFICIENCY,
      parameters,
      token
    );
  }

  /**
   * Convenience method: Fetch utilization data
   */
  async fetchUtilizationData(
    token: string,
    parameters: Record<string, any>
  ): Promise<NATSResponse> {
    return this.request(
      StatementIds.UTILIZATION,
      parameters,
      token
    );
  }

  /**
   * Convenience method: Fetch output data
   */
  async fetchOutputData(
    token: string,
    parameters: Record<string, any>
  ): Promise<NATSResponse> {
    return this.request(
      StatementIds.OUTPUT,
      parameters,
      token
    );
  }

  /**
   * Convenience method: Fetch downtime data
   */
  async fetchDowntimeData(
    token: string,
    parameters: Record<string, any>
  ): Promise<NATSResponse> {
    return this.request(
      StatementIds.DOWNTIME_ACCUMULATED,
      parameters,
      token
    );
  }

  /**
   * Convenience method: Fetch MTBF data
   */
  async fetchMTBFData(
    token: string,
    parameters: Record<string, any>
  ): Promise<NATSResponse> {
    return this.request(
      StatementIds.MTBF,
      parameters,
      token
    );
  }

  /**
   * Convenience method: Fetch target data
   */
  async fetchTargetData(token: string, machineId?: string): Promise<NATSResponse> {
    return this.request(
      StatementIds.TARGET_DATA,
      machineId ? { machine_id: machineId } : {},
      token
    );
  }

  /**
   * Convenience method: Fetch preset data
   */
  async fetchPresetData(
    token: string,
    parameters: Record<string, any>
  ): Promise<NATSResponse> {
    return this.request(
      StatementIds.PRESETS,
      parameters,
      token
    );
  }

  /**
   * Convenience method: Fetch preset detail data
   */
  async fetchPresetDetailData(
    token: string,
    parameters: Record<string, any>
  ): Promise<NATSResponse> {
    return this.request(
      StatementIds.PRESETS_DETAIL,
      parameters,
      token
    );
  }

  /**
   * Send prewarm request to backend after login
   * This notifies the backend to prepare/cache data for the user
   */
  async sendPrewarmRequest(token: string): Promise<NATSResponse> {
    // Get organization alias from token
    const orgAlias = getOrgAlias(token);
    if (!orgAlias) {
      console.error('❌ OrgNATSClient: Could not extract organization alias for prewarm');
      return {
        request_id: generateRequestId(),
        success: false,
        error: 'Could not determine organization from token',
        error_code: 'INVALID_TOKEN',
      };
    }

    // Build the prewarm subject: {org-alias}.prewarm.request
    const subject = `${orgAlias}.prewarm.request`;
    const requestId = `prewarm-${generateRequestId()}`;

    // Build the prewarm payload (minimal - just request_id and token)
    const request = {
      request_id: requestId,
      user_token: token,
    };

    try {
      if (!natsClient.isConnected()) {
        throw new Error('NATS not connected');
      }

      console.log(`🔥 OrgNATSClient: Sending prewarm request to ${subject}`);
      
      // Use a shorter timeout for prewarm - it's fire-and-forget style
      const response = await natsClient.request(subject, request, 5000) as NATSResponse;
      
      console.log(`✅ OrgNATSClient: Prewarm response received:`, response.success);
      return response;

    } catch (error) {
      // Prewarm failure is not critical - log and continue
      console.warn('⚠️ OrgNATSClient: Prewarm request failed (non-critical):', error);
      return {
        request_id: requestId,
        success: false,
        error: error instanceof Error ? error.message : String(error),
        error_code: 'PREWARM_FAILED',
      };
    }
  }
}

// Export singleton instance — survive HMR by persisting on window
export const orgNatsClient: OrgNATSClient =
  (window as any).__orgNatsClient ?? ((window as any).__orgNatsClient = new OrgNATSClient());
